import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";
import {
  SectionCard,
  FormRow,
  FieldInput,
  PrimaryButton,
  GhostButton,
  ContextualHelpDropdown,
  Tile,
  TileGrid,
  Sparkles,
  MessageSquare,
  Phone,
  Zap,
  Check,
  X as XIcon,
} from "@/components/ds";
import IconByName from "@/components/ds/IconByName";

// ─── Types ──────────────────────────────────────────────────────────────────

interface ImportResult {
  success: boolean;
  imported: number;
  skipped: number;
  message?: string;
  error?: string;
}

// ─── Step indicators ─────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: "Welcome" },
  { id: 2, label: "Import macros" },
  { id: 3, label: "Connect commerce" },
  { id: 4, label: "Launch bot" },
];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-2 mb-6">
      {STEPS.map((step, i) => {
        const isCurrent = current === step.id;
        const isDone = current > step.id;
        const dotCls = isCurrent
          ? "bg-[color:var(--color-primary)] text-white"
          : isDone
            ? "bg-emerald-500 text-white"
            : "bg-zinc-200 text-zinc-500";
        const labelCls = isCurrent
          ? "text-zinc-800 font-semibold"
          : isDone
            ? "text-emerald-600"
            : "text-zinc-400";
        return (
          <React.Fragment key={step.id}>
            <div className="flex items-center gap-1.5">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${dotCls}`}
              >
                {isDone ? <Check className="w-3.5 h-3.5" /> : step.id}
              </div>
              <span
                className={`text-xs whitespace-nowrap hidden sm:block ${labelCls}`}
              >
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={`h-px flex-1 ${isDone ? "bg-emerald-300" : "bg-zinc-200"}`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Comparison row helper ───────────────────────────────────────────────────

function BoolMark({ on }: { on: boolean }) {
  return on ? (
    <Check className="w-4 h-4 text-emerald-600 inline-block" />
  ) : (
    <XIcon className="w-4 h-4 text-zinc-300 inline-block" />
  );
}

// ─── Step 1: Welcome / comparison table ──────────────────────────────────────

function Step1Welcome({ onNext }: { onNext(): void }) {
  const rows: Array<{
    feature: string;
    gorgias: boolean | string;
    velaro: boolean | string;
  }> = [
    { feature: "AI-powered responses", gorgias: false, velaro: true },
    { feature: "Canned response import", gorgias: true, velaro: true },
    { feature: "Live agent chat", gorgias: true, velaro: true },
    { feature: "Email ticket support", gorgias: true, velaro: true },
    { feature: "Multi-channel workflows", gorgias: false, velaro: true },
    { feature: "Routing & rules engine", gorgias: false, velaro: true },
    { feature: "Knowledge base with AI search", gorgias: false, velaro: true },
    { feature: "Quote builder (checkout link)", gorgias: false, velaro: true },
    { feature: "Abandoned cart recovery", gorgias: false, velaro: true },
    { feature: "Screen pop (CRM lookup)", gorgias: false, velaro: true },
    { feature: "Price", gorgias: "$300+/mo", velaro: "From $129/mo" },
  ];

  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-zinc-800 mb-2">
          Switch from Gorgias to Velaro in 15 minutes
        </h2>
        <p className="text-zinc-500 text-sm max-w-lg mx-auto">
          Import your canned responses, connect your Shopify store, and launch
          an AI bot that handles the questions your team was answering
          manually — automatically.
        </p>
      </div>

      <div className="rounded-xl border border-zinc-200 overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="px-4 py-3 text-left text-zinc-600 font-semibold w-1/2">
                Feature
              </th>
              <th className="px-4 py-3 text-center text-zinc-500 font-medium w-1/4">
                Gorgias
              </th>
              <th className="px-4 py-3 text-center text-[color:var(--color-primary)] font-semibold w-1/4">
                Velaro
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/50"}>
                <td className="px-4 py-3 text-zinc-700">{row.feature}</td>
                <td className="px-4 py-3 text-center">
                  {typeof row.gorgias === "boolean" ? (
                    <BoolMark on={row.gorgias} />
                  ) : (
                    <span className="text-zinc-600 text-xs">{row.gorgias}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {typeof row.velaro === "boolean" ? (
                    <BoolMark on={row.velaro} />
                  ) : (
                    <span className="text-[color:var(--color-primary)] font-semibold text-xs">
                      {row.velaro}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <PrimaryButton onClick={onNext}>Start migration</PrimaryButton>
      </div>
    </div>
  );
}

// ─── Step 2: Import Gorgias macros ───────────────────────────────────────────

function Step2ImportMacros({
  onNext,
  onSkip,
}: {
  onNext(): void;
  onSkip(): void;
}) {
  const api = useApi();
  const [accountName, setAccountName] = useState("");
  const [email, setEmail] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  async function handleImport() {
    if (!accountName.trim() || !email.trim() || !apiKey.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const resp = await api.admin.post("Gorgias/ImportMacros", {
        gorgiasAccountName: accountName.trim(),
        email: email.trim(),
        apiKey: apiKey.trim(),
      });
      const data = await resp.json();
      setResult(data);
      if (data?.success && (data.imported ?? 0) > 0) {
        setTimeout(() => onNext(), 2000);
      }
    } catch {
      setResult({
        success: false,
        imported: 0,
        skipped: 0,
        error: "Request failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h3 className="text-xl font-bold text-zinc-800 mb-1">
        Import your canned responses (macros)
      </h3>
      <p className="text-zinc-500 text-sm mb-4">
        Velaro will convert your Gorgias macros into bot Q&amp;A pairs. Your
        bot can answer these questions automatically — no human needed.
      </p>

      <div className="space-y-3 mb-4">
        <FormRow label="Gorgias account name" hint="The part before .gorgias.com in your Gorgias URL.">
          <div className="flex items-center gap-2">
            <FieldInput
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="yourstore"
            />
            <span className="text-zinc-400 text-sm whitespace-nowrap">
              .gorgias.com
            </span>
          </div>
        </FormRow>

        <FormRow label="Gorgias account email" hint="The email address you use to log in to Gorgias.">
          <FieldInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourstore.com"
          />
        </FormRow>

        <FormRow label="API key">
          <div>
            <FieldInput
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="••••••••••••••••••••"
            />
            <div className="mt-2 rounded-lg border border-zinc-200 overflow-hidden">
              <ContextualHelpDropdown
                title="How to get your Gorgias API key"
                triggerClassName="!w-full !flex !items-center !justify-between !px-3 !py-2 !text-left !bg-zinc-50 hover:!bg-zinc-100 !text-xs !text-zinc-600 !no-underline !text-inherit"
                panelClassName="!mt-0 !bg-white !border-0 !border-t !border-t-zinc-200 !rounded-none !p-3 !text-xs !space-y-1.5"
                body={
                  <>
                    <p>1. Log in to your Gorgias account</p>
                    <p>
                      2. Go to <strong>Settings</strong> →{" "}
                      <strong>REST API</strong>
                    </p>
                    <p>
                      3. Click <strong>Generate API key</strong>
                    </p>
                    <p>4. Copy the key and paste it here</p>
                    <p className="text-zinc-400 mt-1">
                      Required scope: Read access to Macros is sufficient.
                    </p>
                  </>
                }
              />
            </div>
          </div>
        </FormRow>
      </div>

      {result && (
        <div
          className={`rounded-lg p-3 mb-4 text-sm ${
            result.success
              ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
              : "bg-red-50 border border-red-200 text-red-800"
          }`}
        >
          {result.success
            ? result.imported === 0
              ? (result.message ?? "No importable macros found.")
              : `Imported ${result.imported} macro${result.imported === 1 ? "" : "s"} as bot Q&A${result.skipped > 0 ? ` (${result.skipped} skipped — no message action)` : ""}. Continuing...`
            : (result.error ?? "Import failed. Please try again.")}
        </div>
      )}

      <div className="flex items-center justify-between">
        <GhostButton onClick={onSkip}>Skip this step</GhostButton>
        <PrimaryButton
          onClick={handleImport}
          disabled={
            loading ||
            !accountName.trim() ||
            !email.trim() ||
            !apiKey.trim()
          }
        >
          {loading ? "Importing..." : "Import macros"}
        </PrimaryButton>
      </div>
    </div>
  );
}

// ─── Step 3: Connect commerce platform ───────────────────────────────────────

function Step3ConnectCommerce({ onNext }: { onNext(): void }) {
  const navigate = useNavigate();

  const platforms = [
    { name: "Shopify", id: "Shopify", icon: "ShoppingCart" },
    { name: "BigCommerce", id: "BigCommerce", icon: "Package" },
    { name: "WooCommerce", id: "WooCommerce", icon: "ShoppingCart" },
    { name: "Magento", id: "Magento", icon: "Star" },
    { name: "Square", id: "Square", icon: "CreditCard" },
    { name: "NetSuite", id: "NetSuite", icon: "Briefcase" },
  ];

  return (
    <div>
      <h3 className="text-xl font-bold text-zinc-800 mb-1">
        Connect your commerce platform (optional)
      </h3>
      <p className="text-zinc-500 text-sm mb-6">
        Connect your store to unlock AI-powered order lookups, product search,
        cart building, and return handling — everything your support team was
        doing manually.
      </p>

      <TileGrid columns={{ base: 2, md: 3 }} gap={3}>
        {platforms.map((platform) => (
          <Tile
            key={platform.id}
            onClick={() => navigate(`/integrations/${platform.id}`)}
          >
            <Tile.Body>
              <div className="flex flex-col items-center gap-1.5 py-1">
                <IconByName name={platform.icon} className="w-6 h-6" />
                <Tile.Title>{platform.name}</Tile.Title>
              </div>
            </Tile.Body>
          </Tile>
        ))}
      </TileGrid>

      <div className="mt-6 rounded-lg bg-[color:var(--color-primary)]/10 border border-[color:var(--color-primary)]/30 p-4 text-sm text-zinc-700">
        <strong className="text-zinc-800">What you'll unlock:</strong>
        <ul className="list-disc list-inside mt-1.5 space-y-0.5 text-xs">
          <li>
            Order lookup — "Where is my order?" → bot checks real-time status
          </li>
          <li>Product search — bot searches your catalog and shows results</li>
          <li>
            Pricing &amp; inventory — bot checks stock and quotes prices
            instantly
          </li>
          <li>Returns &amp; refunds — bot initiates return process without human</li>
          <li>Upsell &amp; recommendations — bot suggests complementary products</li>
        </ul>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <GhostButton onClick={onNext}>Skip — I'll connect later</GhostButton>
        <PrimaryButton onClick={onNext}>Continue</PrimaryButton>
      </div>
    </div>
  );
}

// ─── Step 4: Launch the bot ───────────────────────────────────────────────────

function Step4Launch({ onOpenWizard }: { onOpenWizard(): void }) {
  const navigate = useNavigate();

  const actions = [
    {
      key: "qna",
      icon: <MessageSquare className="w-6 h-6 text-[color:var(--color-primary)]" />,
      title: "Review imported Q&A",
      desc: "View and edit the Gorgias macros that were converted to bot Q&A pairs.",
      onClick: () => navigate("/integrations/Gorgias"),
    },
    {
      key: "channels",
      icon: <Phone className="w-6 h-6 text-[color:var(--color-primary)]" />,
      title: "Add channels",
      desc: "Add chat, email, SMS, or phone to reach customers where they are.",
      onClick: () => navigate("/integrations"),
    },
    {
      key: "wizard",
      icon: <Zap className="w-6 h-6 text-[color:var(--color-primary)]" />,
      title: "Launch starter wizard",
      desc: "Create your first support workflow in 3 clicks.",
      onClick: onOpenWizard,
    },
  ];

  return (
    <div>
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[color:var(--color-primary)]/10 mb-3">
          <Sparkles className="w-7 h-7 text-[color:var(--color-primary)]" />
        </div>
        <h3 className="text-xl font-bold text-zinc-800 mb-1">
          You're ready to go live
        </h3>
        <p className="text-zinc-500 text-sm">
          Three actions to launch your Velaro bot and start handling
          conversations automatically.
        </p>
      </div>

      <TileGrid columns={{ base: 1, md: 3 }} gap={3}>
        {actions.map((a) => (
          <Tile key={a.key} onClick={a.onClick}>
            <Tile.Body>
              <div className="mb-2">{a.icon}</div>
              <Tile.Title>{a.title}</Tile.Title>
              <Tile.Description>{a.desc}</Tile.Description>
            </Tile.Body>
          </Tile>
        ))}
      </TileGrid>

      <div className="mt-6 rounded-lg bg-zinc-50 border border-zinc-200 p-4 text-xs text-zinc-500">
        <strong className="text-zinc-600">Cancelling Gorgias:</strong> Your
        Gorgias subscription can be cancelled after you confirm the bot handles
        your volume. Most merchants see full coverage within 2 weeks — start by
        running both in parallel, then cancel Gorgias once you're confident.
      </div>
    </div>
  );
}

// ─── Main Wizard Component ────────────────────────────────────────────────────

export default function GorgiasWizard() {
  const [step, setStep] = useState(1);
  const [showWorkflowWizard, setShowWorkflowWizard] = useState(false);
  const [tab, setTab] = useState<"overview" | "log">("overview");

  const tabsJSX = (
    <>
      <IntegrationTab active={tab === "overview"} onClick={() => setTab("overview")}>Overview</IntegrationTab>
      <IntegrationTab active={tab === "log"} onClick={() => setTab("log")}>Activity Log</IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="Gorgias Migration" tabs={tabsJSX}>
      {showWorkflowWizard && (
        <StarterWorkflowWizard
          integration="shopify"
          onClose={() => setShowWorkflowWizard(false)}
        />
      )}

      {tab === "overview" && (
        <div className="max-w-2xl mx-auto">
          <SectionCard
            title="Gorgias to Velaro migration"
            description="Import macros, connect Shopify, launch bot"
          >
            <StepIndicator current={step} />

            {step === 1 && <Step1Welcome onNext={() => setStep(2)} />}
            {step === 2 && (
              <Step2ImportMacros
                onNext={() => setStep(3)}
                onSkip={() => setStep(3)}
              />
            )}
            {step === 3 && <Step3ConnectCommerce onNext={() => setStep(4)} />}
            {step === 4 && (
              <Step4Launch onOpenWizard={() => setShowWorkflowWizard(true)} />
            )}
          </SectionCard>

          {step > 1 && step < 4 && (
            <div className="flex justify-start mt-3">
              <GhostButton onClick={() => setStep((s) => s - 1)}>Back</GhostButton>
            </div>
          )}
        </div>
      )}

      {tab === "log" && (
        <IntegrationActivityLog integrationSlug="Gorgias" />
      )}
    </StickyRouteHeader>
  );
}

export function BackToIntegrationLandingGorgias() {
  const navigate = useNavigate();
  return (
    <GhostButton onClick={() => navigate("/integrations")}>
      Back to Integrations
    </GhostButton>
  );
}
